import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import { GetProDocDocumentRepliesResponse } from '@dreamai/types';
import CommentCountButton from './CommentCountButton';

interface ProDocCommentButtonProps {
  proDocId: string;
  isExpanded: boolean;
  onClick: () => void;
}

/**
 * Comment button for ProDoc-level replies (not item-level)
 * Manages fetching and displaying the reply count
 */
const ProDocCommentButton: React.FC<ProDocCommentButtonProps> = ({
  proDocId,
  isExpanded,
  onClick,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [replyCount, setReplyCount] = useState(0);
  const [hasLoadedCount, setHasLoadedCount] = useState(false);

  // Fetch reply count for this ProDoc
  const fetchReplyCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetProDocDocumentRepliesResponse>(
        `/pro-docs/${proDocId}/replies`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.replies) {
        setReplyCount(response.data.replies.length);
        setHasLoadedCount(true);
      }
    } catch (err) {
      console.error('Error fetching ProDoc reply count:', err);
    }
  }, [proDocId, getAccessTokenSilently]);

  // Load reply count when component mounts
  useEffect(() => {
    fetchReplyCount();
  }, [fetchReplyCount]);

  return (
    <CommentCountButton
      count={replyCount}
      hasLoaded={hasLoadedCount}
      isExpanded={isExpanded}
      onClick={onClick}
    />
  );
};

export default ProDocCommentButton;
